import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

export type CheckboxOption = {
  label: React.ReactNode;
  value: string;
};

export type CheckboxGroupInputProps = {
  options: CheckboxOption[];
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
  maxSelections?: number;
};

export function CheckboxGroupInput({
  options,
  value,
  onChange,
  disabled = false,
  maxSelections,
}: CheckboxGroupInputProps) {
  const selectedValues = Array.isArray(value) ? value : [];

  const allSelected =
    options.length > 0 && selectedValues.length === options.length;
  const someSelected = selectedValues.length > 0 && !allSelected;

  const handleSelectAll = (checked: boolean) => {
    let newValue = checked ? options.map((option) => option.value) : [];
    if (maxSelections && newValue.length > maxSelections) {
      newValue = newValue.slice(0, maxSelections);
    }
    onChange?.(newValue);
  };

  const handleOptionToggle = (
    optionValue: string,
    checked: boolean | "indeterminate",
  ) => {
    if (checked === "indeterminate") return;
    const newValue = checked
      ? maxSelections === 1
        ? [optionValue]
        : [...selectedValues, optionValue]
      : selectedValues.filter((item) => item !== optionValue);
    if (
      maxSelections &&
      checked &&
      maxSelections !== 1 &&
      newValue.length > maxSelections
    ) {
      return;
    }

    onChange?.(newValue);
  };

  return (
    <div className={`flex flex-col rounded-md border`}>
      {/* "Select All" Checkbox */}
      <div className="flex items-center space-x-3 p-4 pb-2">
        <Checkbox
          id="select-all-controller"
          checked={someSelected ? "indeterminate" : allSelected}
          onCheckedChange={handleSelectAll}
          disabled={disabled}
        />
        <Label
          htmlFor="select-all-controller"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          Select all
        </Label>
      </div>

      {/* Divider */}
      <hr className="mx-4" />

      {/* Individual Option Checkboxes - Scrollable */}
      <div className="flex flex-col space-y-2 p-4 pt-2 max-h-60 overflow-y-auto">
        {options.map((option) => {
          // Create a unique and valid ID for each checkbox's label association.
          const optionId = `checkbox-option-${option.value.replace(/\s+/g, "-")}`;
          return (
            <div key={option.value} className="flex items-center space-x-3">
              <Checkbox
                id={optionId}
                checked={selectedValues.includes(option.value)}
                onCheckedChange={(checked) => {
                  handleOptionToggle(option.value, checked === true);
                }}
                disabled={disabled}
              />
              <Label
                htmlFor={optionId}
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {option.label}
              </Label>
            </div>
          );
        })}
      </div>
    </div>
  );
}
